import { WorkerMessageType as WMT } from './WorkerMessageType';
import {
    createWorkerRevisionGuard,
    type WorkerRevisionGuard,
    type WorkerRevisionGuardState,
    type WorkerRevisionPurpose,
    type WorkerRevisionToken,
} from '../history/worker';
import {
    createStaleWorkerResponseError,
    isStaleWorkerResponseError,
} from './StaleWorkerResponseError';
import type { StepExportRequest, StepExportResult } from '../export/step/types';
import type {
    BeginWorkerGeometryRuntimeSwitchInput,
    WorkerGeometryRuntimeSwitchCommitInput,
    WorkerGeometryRuntimeSwitchHandle,
} from './session/WorkerGeometryRuntimeSwitchTypes';
import type {
    QueryConvertEntitiesFaceBoundariesPayload,
    QueryConvertEntitiesFaceBoundariesResult,
    QueryConvertEntitiesInnerLoopByEdgePayload,
    QueryConvertEntitiesInnerLoopByEdgeResult,
} from '../../types/reference/sketch-entity-reference/TopologyFaceBoundaryRuntime';
export {
    isStaleWorkerResponseError,
    StaleWorkerResponseError,
} from './StaleWorkerResponseError';

/**
 * 待处理的请求
 */
interface PendingRequest {
    resolve: (value: any) => void;
    reject: (error: Error) => void;
    timestamp: number;
    revisionToken: WorkerRevisionToken;
    markStaleResponseHandled: () => void;
    /** stale 响应拒绝前必须完成的 Worker 侧补偿。 */
    readonly onStaleResponse?: (responseValue: unknown) => void | Promise<void>;
    /** 清理型请求即使 revision 已推进，也应按真实 Worker 结果完成。 */
    readonly acceptStaleResponse: boolean;
}

/**
 * 发送 Worker 请求时的内部控制选项。
 */
interface SendRequestOptions {
    /**
     * 标记由上层组合出来的外层 Promise 已经处理 stale 拒绝。
     */
    readonly markOuterStaleResponseHandled?: () => void;
    /** stale 响应拒绝前执行的补偿回调。 */
    readonly onStaleResponse?: (responseValue: unknown) => void | Promise<void>;
    /** 是否接受清理型请求的 stale 响应。 */
    readonly acceptStaleResponse?: boolean;
}

/**
 * Worker runtime switch 句柄的本地生命周期状态。
 */
type RuntimeSwitchHandleState = 'active' | 'batching' | 'committing' | 'committed' | 'aborting' | 'aborted' | 'closed';

/**
 * Worker runtime switch 在 WorkerManager 内部的占用阶段。
 */
type RuntimeSwitchSessionPhase = 'beginning' | 'active' | 'batching' | 'committing' | 'aborting';

/**
 * WorkerManager 当前持有的 runtime switch 会话占用记录。
 */
interface RuntimeSwitchSessionRecord {
    /** runtime switch 会话 ID。 */
    readonly id: string;
    /** 会话开始时绑定的 document revision。 */
    readonly documentRevision: number;
    /** 当前会话占用阶段。 */
    phase: RuntimeSwitchSessionPhase;
}

/**
 * Worker 放弃 runtime switch 的真实返回结果。
 */
interface RuntimeSwitchAbortResult {
    /** runtime switch 会话 ID。 */
    readonly runtimeSwitchId: string;
    /** Worker 是否实际放弃了匹配的 staged runtime。 */
    readonly aborted: boolean;
}

/**
 * Worker 初始化生命周期令牌。
 */
type WorkerInitializationToken = symbol;

/**
 * WorkerManager 配置
 */
export interface WorkerManagerOptions {
    /** 请求超时时间（毫秒） */
    timeout?: number;
    /** 是否自动初始化 */
    autoInit?: boolean;
    /** 当前文档 ID；用于拦截 undo/redo 后过期的 Worker 响应。 */
    documentId?: string;
    /** 初始文档 revision。 */
    initialDocumentRevision?: number;
    /** 初始历史序号。 */
    initialHistorySeq?: number;
    /** 是否只接受同一 revision 下的最新请求。 */
    retirePreviousWorkerRequests?: boolean;
}

const DEFAULT_WORKER_REQUEST_TIMEOUT_MS = 1800000;
let count = 0;
let runtimeSwitchCount = 0;
let importedBrepRegistrationTransactionCount = 0;

/**
 * 创建可诊断的 runtime switch 会话 ID。
 *
 * @returns runtime switch 会话 ID。
 */
function createGeometryRuntimeSwitchId(): string {
    const currentCount = runtimeSwitchCount;
    runtimeSwitchCount += 1;

    return `runtime-switch:${Date.now()}:${currentCount}`;
}

/**
 * 创建可诊断的 active BRep 注册事务 ID。
 *
 * @returns active BRep 注册事务 ID。
 */
function createImportedBrepRegistrationTransactionId(): string {
    const currentCount = importedBrepRegistrationTransactionCount;
    importedBrepRegistrationTransactionCount += 1;
    return `imported-brep-registration:${Date.now()}:${currentCount}`;
}

/**
 * 解析 Worker 请求用途。
 *
 * @param type Worker 消息类型。
 * @returns revision guard 用途。
 */
function resolveWorkerRevisionPurpose(type: WorkerMessageType): WorkerRevisionPurpose {
    if (type === WMT.INIT) {
        return 'worker-lifecycle';
    }

    if (
        type === WMT.EXTRUDE_PREVIEW
        || type === WMT.EXTRUDE_PREVIEW_IN_SANDBOX
        || type === WMT.FILLET_PREVIEW
        || type === WMT.FILLET_PREVIEW_IN_SANDBOX
        || type === WMT.CHAMFER_PREVIEW
        || type === WMT.CHAMFER_PREVIEW_IN_SANDBOX
        || type === WMT.SHELL_PREVIEW
        || type === WMT.SHELL_PREVIEW_IN_SANDBOX
        || type === WMT.RIB_PREVIEW
        || type === WMT.RIB_PREVIEW_IN_SANDBOX
        || type === WMT.REVOLVE_PREVIEW
        || type === WMT.REVOLVE_PREVIEW_IN_SANDBOX
    ) {
        return 'feature-preview';
    }

    if (type === WMT.GENERATE_MESH) {
        return 'mesh-build';
    }

    if (
        type === WMT.EXECUTE_COMMAND
        || type === WMT.BATCH_EXECUTE
        || type === WMT.EXECUTE_FEATURE_PREFIX_BATCH
        || type === WMT.QUERY_TOPOLOGY_CHECKPOINT
        || type === WMT.QUERY_CONVERT_ENTITIES_FACE_BOUNDARIES
        || type === WMT.QUERY_CONVERT_ENTITIES_INNER_LOOP_BY_EDGE
        || type === WMT.REGISTER_IMPORTED_BREP_SHAPE
        || type === WMT.REGISTER_IMPORTED_BREP_SHAPES_ATOMICALLY
        || type === WMT.COMPLETE_IMPORTED_BREP_REGISTRATION_TRANSACTION
        || type === WMT.BEGIN_GEOMETRY_RUNTIME_SWITCH
        || type === WMT.REGISTER_IMPORTED_BREP_SHAPE_IN_RUNTIME
        || type === WMT.REGISTER_IMPORTED_BREP_SHAPES_ATOMICALLY_IN_RUNTIME
        || type === WMT.BATCH_EXECUTE_IN_RUNTIME
        || type === WMT.COMMIT_GEOMETRY_RUNTIME_SWITCH
        || type === WMT.ABORT_GEOMETRY_RUNTIME_SWITCH
    ) {
        return 'feature-rebuild';
    }

    return 'custom';
}

/**
 * Geometry Worker 管理器类
 */
export class WorkerManager {
    private worker: Worker | null = null;
    private pendingRequests: Map<string, PendingRequest> = new Map();
    private isInitialized = false;
    private initPromise: Promise<void> | null = null;
    private options: Required<WorkerManagerOptions>;
    private readonly revisionGuard: WorkerRevisionGuard;
    private activeGeometryRuntimeSwitchSession: RuntimeSwitchSessionRecord | null = null;
    /** active BRep 批次必须串行，避免第二批在第一批主线程确认前进入同一 GeometryState。 */
    private activeImportedBrepRegistrationQueue: Promise<void> = Promise.resolve();
    /** 当前 active BRep 批次是否处于 Worker 准备到主线程确认之间。 */
    private isActiveImportedBrepRegistrationTransactionInFlight = false;
    /** 当前仍有权写入 Worker 初始化结果的生命周期令牌。 */
    private activeWorkerInitializationToken: WorkerInitializationToken | null = null;

    constructor(options: WorkerManagerOptions = {}) {
        this.options = {
            timeout: options.timeout ?? DEFAULT_WORKER_REQUEST_TIMEOUT_MS,
            autoInit: options.autoInit ?? true,
            documentId: options.documentId ?? 'default-document',
            initialDocumentRevision: options.initialDocumentRevision ?? 0,
            initialHistorySeq: options.initialHistorySeq ?? 0,
            retirePreviousWorkerRequests: options.retirePreviousWorkerRequests ?? false,
        };
        this.revisionGuard = createWorkerRevisionGuard({
            documentId: this.options.documentId,
            initialDocumentRevision: this.options.initialDocumentRevision,
            initialHistorySeq: this.options.initialHistorySeq,
            retirePreviousRequests: this.options.retirePreviousWorkerRequests,
        });

        if (this.options.autoInit) {
            void this.init();
        }
    }

    /**
     * 初始化 Worker
     */
    async init(): Promise<void> {
        if (this.isInitialized) {
            console.log('WorkerManager 已初始化');
            return;
        }

        if (this.initPromise) {
            return this.initPromise;
        }

        const initializationToken = Symbol('WorkerManager.init');
        this.activeWorkerInitializationToken = initializationToken;
        this.initPromise = this._doInit(initializationToken);
        return this.initPromise;
    }

    /**
     * 执行 Worker 初始化。
     *
     * @param initializationToken 本次初始化的生命周期令牌。
     */
    private async _doInit(initializationToken: WorkerInitializationToken): Promise<void> {
        let worker: Worker | null = null;

        try {
            //------ Worker 文件集中迁移 开始------
            // 创建 Worker 实例
            worker = new Worker(
                new URL('./GeometryWorker.worker.ts', import.meta.url),
                {
                    type: 'module',
                },
            );
            this.worker = worker;
            //------ Worker 文件集中迁移 结束------

            // 设置消息监听器
            worker.addEventListener('message', this.handleWorkerMessage.bind(this));

            // 设置错误监听器
            worker.addEventListener('error', this.handleWorkerError.bind(this));

            // 发送初始化消息
            await this.sendRequest(WMT.INIT, {});

            if (!this.isCurrentWorkerInitialization(initializationToken, worker)) {
                throw new Error('Worker 初始化已被新的生命周期替换');
            }

            this.isInitialized = true;
            this.activeWorkerInitializationToken = null;
        } catch (error) {
            const ownsCurrentInitialization = worker !== null && this.isCurrentWorkerInitialization(
                initializationToken,
                worker,
            );
            const tokenStillCurrent = this.activeWorkerInitializationToken === initializationToken;

            if (ownsCurrentInitialization || tokenStillCurrent) {
                console.error('❌ WorkerManager 初始化失败:', error);
            }

            if (ownsCurrentInitialization) {
                // 只允许本次 init 清理自己创建且仍归属自己的 Worker，避免旧 catch 误杀重启后的新 Worker。
                worker.terminate();
                this.worker = null;
                this.isInitialized = false;
                this.initPromise = null;
                this.activeWorkerInitializationToken = null;
            } else if (tokenStillCurrent) {
                // Worker 构造或归属校验失败时，仍需释放本次初始化令牌，保证后续可以重试。
                this.isInitialized = false;
                this.initPromise = null;
                this.activeWorkerInitializationToken = null;
            }
            throw error;
        }
    }

    /**
     * 判断指定 Worker 是否仍归属于本次初始化。
     *
     * @param initializationToken 初始化生命周期令牌。
     * @param worker 本次初始化创建的 Worker。
     * @returns 是否仍是当前初始化拥有的 Worker。
     */
    private isCurrentWorkerInitialization(
        initializationToken: WorkerInitializationToken,
        worker: Worker,
    ): boolean {
        return this.activeWorkerInitializationToken === initializationToken && this.worker === worker;
    }

    /**
     * 处理 Worker 消息
     */
    private async handleWorkerMessage(event: MessageEvent<WorkerResponse>): Promise<void> {
        const response = event.data;
        const { id, success, result, data, error } = response;

        const pending = this.pendingRequests.get(id);
        if (!pending) {
            console.log(`收到未知请求的响应: ${id}`);
            return;
        }

        // 移除待处理请求
        this.pendingRequests.delete(id);

        const responseValue = data !== undefined ? data : result;
        const decision = this.revisionGuard.shouldAccept(pending.revisionToken);
        if (!decision.accepted && !pending.acceptStaleResponse) {
            pending.markStaleResponseHandled();
            try {
                await pending.onStaleResponse?.(responseValue);
            } catch (cleanupError) {
                const cleanupMessage = cleanupError instanceof Error ? cleanupError.message : String(cleanupError);
                pending.reject(new Error(
                    `Worker stale 响应补偿失败: ${cleanupMessage}`,
                    {cause: createStaleWorkerResponseError(decision)},
                ));
                return;
            }
            pending.reject(createStaleWorkerResponseError(decision));
            return;
        }

        if (success) {
            // 优先使用 data 字段，如果没有则使用 result 字段（兼容旧代码）
            pending.resolve(responseValue);
        } else {
            pending.reject(new Error(error || '未知错误'));
        }
    }

    /**
     * 处理 Worker 错误
     */
    private handleWorkerError(event: ErrorEvent): void {
        // 收集 ErrorEvent 上所有可用的错误信息
        const details = [
            event.message && `message: ${event.message}`,
            event.filename && `file: ${event.filename}:${event.lineno}:${event.colno}`,
            event.error && `error: ${event.error}`,
        ].filter(Boolean).join('; ');
        const errorMessage = details || 'Worker 发生未知错误（ErrorEvent 无详细信息，请检查浏览器控制台）';
        console.error('Worker 错误:', errorMessage, event);

        // 拒绝所有待处理的请求，携带真实错误信息
        this.pendingRequests.forEach((pending, id) => {
            pending.reject(new Error(`Worker 发生错误: ${errorMessage}`));
            this.pendingRequests.delete(id);
        });
    }

    private toCloneablePayload<T>(payload: T): T {
        try {
            return structuredClone(payload);
        } catch (cloneError) {
            try {
                return JSON.parse(JSON.stringify(payload)) as T;
            } catch (jsonError) {
                const cloneReason = cloneError instanceof Error ? cloneError.message : String(cloneError);
                const jsonReason = jsonError instanceof Error ? jsonError.message : String(jsonError);
                throw new Error(`Worker payload is not cloneable. structuredClone: ${cloneReason}; jsonFallback: ${jsonReason}`);
            }
        }
    }

    /**
     * 只在 stale 分支触发时为 Promise 挂载内部处理器。
     *
     * @param promise 需要标记 stale 已处理的 Promise。
     */
    private markPromiseStaleResponseHandled<T>(promise: Promise<T> | null): void {
        if (!promise) {
            return;
        }

        /**
         * 这里不能在创建 Promise 时统一挂 catch，否则真实 Worker 错误会被内部处理器二次改写为
         * unhandled rejection。只有 stale 已经被 revision guard 明确判定为预期取消时，才挂内部处理器。
         */
        void promise.catch((error) => {
            if (isStaleWorkerResponseError(error)) {
                return;
            }
            throw error;
        });
    }

    private sendRequest<T = any>(
        type: WorkerMessageType,
        payload: any,
        options: SendRequestOptions = {},
    ): Promise<T> {
        if (!this.worker) {
            return Promise.reject(new Error('Worker 未初始化'));
        }

        const requestId = String(count);
        count += 1;
        const revisionToken = this.revisionGuard.createToken({
            purpose: resolveWorkerRevisionPurpose(type),
            requestId,
        });
        const request: WorkerRequest = {
            id: requestId,
            type,
            payload: this.toCloneablePayload(payload),
            revisionToken,
        };

        let requestPromise: Promise<T> | null = null;
        const markStaleResponseHandled = (): void => {
            this.markPromiseStaleResponseHandled(requestPromise);
            options.markOuterStaleResponseHandled?.();
        };

        requestPromise = new Promise<T>((resolve, reject) => {
            // 记录待处理请求
            this.pendingRequests.set(requestId, {
                resolve,
                reject,
                timestamp: Date.now(),
                revisionToken,
                markStaleResponseHandled,
                onStaleResponse: options.onStaleResponse,
                acceptStaleResponse: options.acceptStaleResponse === true,
            });

            // 设置超时
            setTimeout(() => {
                const pending = this.pendingRequests.get(requestId);
                if (pending) {
                    this.pendingRequests.delete(requestId);
                    reject(new Error(`请求超时: ${type}`));
                }
            }, this.options.timeout);

            // 发送消息
            try {
                this.worker!.postMessage(request);
            } catch (error) {
                this.pendingRequests.delete(requestId);
                const message = error instanceof Error ? error.message : String(error);
                reject(new Error(`发送 Worker 请求失败: ${message}`));
            }
        });

        return requestPromise;
    }

    /**
     * 在 Worker 初始化完成后发送请求。
     *
     * 设计说明：
     * 1. Worker 已就绪时直接返回 `sendRequest` 的原始 Promise，避免 `async/await` 再包一层外层 Promise；
     * 2. 这样 stale 响应在底层已挂载的内部处理器可以覆盖对外返回值，不会因为外层 Promise 未处理而污染控制台；
     * 3. 未初始化时仍走初始化 Promise 链，保持懒初始化语义。
     *
     * @param type Worker 消息类型。
     * @param payload Worker 请求载荷。
     * @returns Worker 响应 Promise。
     */
    private sendManagedRequest<T = any>(
        type: WorkerMessageType,
        payload: any,
        options: SendRequestOptions = {},
    ): Promise<T> {
        if (this.isInitialized) {
            return this.sendRequest<T>(type, payload, options);
        }

        let managedPromise: Promise<T> | null = null;
        const markManagedStaleResponseHandled = (): void => {
            this.markPromiseStaleResponseHandled(managedPromise);
        };

        managedPromise = this.ensureInitialized().then(() => this.sendRequest<T>(
            type,
            payload,
            {
                ...options,
                markOuterStaleResponseHandled: markManagedStaleResponseHandled,
            },
        ));

        return managedPromise;
    }

    /**
     * 读取当前 Worker revision guard 状态。
     *
     * @returns revision guard 状态。
     */
    getRevisionState(): WorkerRevisionGuardState {
        return this.revisionGuard.getState();
    }

    /**
     * 设置 Worker 请求上下文。
     *
     * 设计说明：
     * 1. undo/redo、项目恢复或 checkpoint 恢复会推进 historySeq / documentRevision；
     * 2. 主线程必须先推进上下文，再允许异步 Worker 响应写回 Viewer；
     * 3. 该函数只推进 epoch，不直接修改 Worker 几何状态。
     *
     * @param input revision 上下文。
     * @returns 更新后的 guard 状态。
     */
    setRevisionContext(input: {
        documentRevision?: number;
        historySeq?: number;
    }): WorkerRevisionGuardState {
        if (typeof input.documentRevision === 'number') {
            this.revisionGuard.advanceDocumentRevision(input.documentRevision);
        }

        if (typeof input.historySeq === 'number') {
            this.revisionGuard.advanceHistorySeq(input.historySeq);
        }

        return this.revisionGuard.getState();
    }

    /**
     * 推进文档 revision。
     *
     * @param documentRevision 指定 revision；不传则在当前值基础上加一。
     * @returns 更新后的 guard 状态。
     */
    advanceDocumentRevision(documentRevision?: number): WorkerRevisionGuardState {
        const currentState = this.revisionGuard.getState();
        return this.revisionGuard.advanceDocumentRevision(
            documentRevision ?? currentState.documentRevision + 1,
        );
    }

    /**
     * 推进历史序号。
     *
     * @param historySeq 指定历史序号；不传则在当前值基础上加一。
     * @returns 更新后的 guard 状态。
     */
    advanceHistorySeq(historySeq?: number): WorkerRevisionGuardState {
        const currentState = this.revisionGuard.getState();
        return this.revisionGuard.advanceHistorySeq(
            historySeq ?? currentState.historySeq + 1,
        );
    }

    /**
     * 释放已经因 document revision 漂移而无法继续使用的空闲 runtime switch 会话占用。
     *
     * 设计说明：
     * 1. 只有 active 阶段可以被这里释放，因为 committing/aborting/beginning 仍有 Worker 请求在飞行中；
     * 2. 释放占用不向 Worker 发送消息，下一次 begin 会由 Worker 内部 abort 旧 staged runtime。
     */
    private releaseDriftedActiveGeometryRuntimeSwitchSession(): void {
        const session = this.activeGeometryRuntimeSwitchSession;
        if (!session || session.phase !== 'active') {
            return;
        }

        if (session.documentRevision === this.getRevisionState().documentRevision) {
            return;
        }

        this.activeGeometryRuntimeSwitchSession = null;
    }

    /**
     * 断言当前没有未完成的 runtime switch 会话占用。
     */
    private assertNoPendingGeometryRuntimeSwitchSession(): void {
        this.releaseDriftedActiveGeometryRuntimeSwitchSession();

        if (!this.activeGeometryRuntimeSwitchSession) {
            return;
        }

        throw new Error(`Worker runtime switch(${this.activeGeometryRuntimeSwitchSession.id}) 尚未完成，不能开始新的 runtime switch`);
    }

    /**
     * 断言当前可以修改 active Worker 几何状态。
     *
     * @param operation 即将执行的 active 几何写操作名称。
     */
    private assertCanMutateActiveGeometry(operation: string): void {
        this.releaseDriftedActiveGeometryRuntimeSwitchSession();

        if (!this.activeGeometryRuntimeSwitchSession
            && !this.isActiveImportedBrepRegistrationTransactionInFlight) {
            return;
        }

        if (this.isActiveImportedBrepRegistrationTransactionInFlight) {
            throw new Error(`active BRep 注册事务尚未完成，不能执行 active geometry mutation: ${operation}`);
        }

        throw new Error(`Worker runtime switch(${this.activeGeometryRuntimeSwitchSession.id}) 正在占用几何运行时，不能执行 active geometry mutation: ${operation}`);
    }

    /**
     * 按会话 ID 释放 runtime switch 会话占用。
     *
     * @param runtimeSwitchId runtime switch 会话 ID。
     */
    private releaseGeometryRuntimeSwitchSession(runtimeSwitchId: string): void {
        if (this.activeGeometryRuntimeSwitchSession?.id !== runtimeSwitchId) {
            return;
        }

        this.activeGeometryRuntimeSwitchSession = null;
    }

    /**
     * 执行 CAD 命令
     */
    executeCommand(command: CadCommand): Promise<{
        objectId: string;
        objectIds?: string[];
        type: string;
        metadata: Record<string, any>;
    }> {
        try {
            this.assertCanMutateActiveGeometry('executeCommand');
        } catch (error) {
            return Promise.reject(error);
        }
        return this.sendManagedRequest(WMT.EXECUTE_COMMAND, { command });
    }

    /**
     * 生成网格数据
     */
    generateMesh(
        objectId: string,
        options?: {
            tolerance?: number;
            angularTolerance?: number;
        }
    ): Promise<MeshData> {
        return this.sendManagedRequest<MeshData>(WMT.GENERATE_MESH, {
            objectId,
            options
        });
    }

    /**
     * 仅恢复导入 BRep Shape，并注册为 STEP 导出可用的 Worker 基线几何。
     *
     * @param payload 导入 BRep Shape 注册请求。
     * @returns 注册后的 Worker 几何对象 ID。
     */
    registerImportedBrepShape(
        payload: RegisterImportedBrepShapePayload,
    ): Promise<RegisterImportedBrepShapeResult> {
        try {
            this.assertCanMutateActiveGeometry('registerImportedBrepShape');
        } catch (error) {
            return Promise.reject(error);
        }
        return this.sendManagedRequest<RegisterImportedBrepShapeResult>(WMT.REGISTER_IMPORTED_BREP_SHAPE, payload);
    }

    /**
     * 在 active Worker GeometryState 中原子注册一组导入 BRep Shape。
     *
     * @param payloads 已完成主线程 sidecar 预校验的 BRep 注册请求。
     * @returns 与请求顺序一致的 Worker 几何对象注册结果。
     */
    registerImportedBrepShapesAtomically(
        payloads: readonly RegisterImportedBrepShapePayload[],
        options?: {
            readonly commitPreparedResults?: (
                results: readonly RegisterImportedBrepShapeResult[],
            ) => void;
        },
    ): Promise<readonly RegisterImportedBrepShapeResult[]> {
        const executeRegistration = (): Promise<readonly RegisterImportedBrepShapeResult[]> => (
            this.executeActiveImportedBrepRegistrationTransaction(payloads, options)
        );
        const registrationPromise = this.activeImportedBrepRegistrationQueue.then(
            executeRegistration,
            executeRegistration,
        );
        this.activeImportedBrepRegistrationQueue = registrationPromise.then(
            () => undefined,
            () => undefined,
        );
        return registrationPromise;
    }

    /**
     * 执行一个带主线程确认点的 active BRep 注册事务。
     *
     * @param payloads 已完成 sidecar 预校验的注册请求。
     * @param options Worker 准备成功后的同步主线程提交钩子。
     * @returns 与请求顺序一致的注册结果。
     */
    private async executeActiveImportedBrepRegistrationTransaction(
        payloads: readonly RegisterImportedBrepShapePayload[],
        options?: {
            readonly commitPreparedResults?: (
                results: readonly RegisterImportedBrepShapeResult[],
            ) => void;
        },
    ): Promise<readonly RegisterImportedBrepShapeResult[]> {
        if (payloads.length === 0) {
            options?.commitPreparedResults?.([]);
            return [];
        }

        this.assertCanMutateActiveGeometry('registerImportedBrepShapesAtomically');

        this.isActiveImportedBrepRegistrationTransactionInFlight = true;
        try {
            const transactionId = createImportedBrepRegistrationTransactionId();
            const abortTransaction = async (): Promise<void> => {
                await this.completeImportedBrepRegistrationTransaction(transactionId, 'abort', false);
            };
            const results = await this.sendManagedRequest<RegisterImportedBrepShapeResult[]>(
                WMT.REGISTER_IMPORTED_BREP_SHAPES_ATOMICALLY,
                {
                    transactionId,
                    registrations: payloads,
                },
                {
                    // Worker 已经写入 provisional Shape 后若主线程拒绝 stale 响应，必须先补偿再拒绝。
                    onStaleResponse: abortTransaction,
                },
            );

            try {
                if (results.length !== payloads.length) {
                    throw new Error(
                        `Worker BRep 原子注册结果数量不匹配: expected=${payloads.length}; actual=${results.length}`,
                    );
                }
                results.forEach((result, index) => {
                    const expectedObjectId = payloads[index]?.registerGeometryState.objectId.trim();
                    if (result.geometryObjectId !== expectedObjectId) {
                        throw new Error(
                            `Worker BRep 原子注册结果对象 ID 不匹配: index=${index}; expected=${expectedObjectId}; actual=${result.geometryObjectId}`,
                        );
                    }
                });
                // 该钩子同步完成 current 校验与 topo 提交；抛错时 Worker 事务仍可整体补偿。
                options?.commitPreparedResults?.(results);
            } catch (error) {
                await abortTransaction();
                throw error;
            }

            await this.completeImportedBrepRegistrationTransaction(transactionId, 'commit', true);
            return results;
        } finally {
            this.isActiveImportedBrepRegistrationTransactionInFlight = false;
        }
    }

    /**
     * 完成 active Worker BRep 注册事务。
     *
     * @param transactionId 目标事务 ID。
     * @param action 提交或补偿动作。
     * @param requireCompleted 是否要求 Worker 必须仍持有目标事务。
     */
    private async completeImportedBrepRegistrationTransaction(
        transactionId: string,
        action: CompleteImportedBrepRegistrationTransactionAction,
        requireCompleted: boolean,
    ): Promise<void> {
        const result = await this.sendManagedRequest<CompleteImportedBrepRegistrationTransactionResult>(
            WMT.COMPLETE_IMPORTED_BREP_REGISTRATION_TRANSACTION,
            { transactionId, action },
            {
                // 该消息只释放/确认已由 ID 隔离的事务，不能因再次推进 revision 而阻断清理。
                acceptStaleResponse: true,
            },
        );
        if (result.transactionId !== transactionId || result.action !== action) {
            throw new Error(
                `Worker BRep 注册事务完成响应不匹配: expected=${transactionId}/${action}; actual=${result.transactionId}/${result.action}`,
            );
        }
        if (requireCompleted && result.completed !== true) {
            throw new Error(`Worker BRep 注册事务未完成: transactionId=${transactionId}; action=${action}`);
        }
    }

    /**
     * 开始 Worker 几何运行时切换，并返回受控的会话句柄。
     *
     * 设计说明：
     * 1. 开始切换前必须推进 document revision，用新的 revision 隔离旧文档或旧模型的异步响应；
     * 2. 调用方只能通过返回的句柄注册、提交或放弃 staged runtime，不能直接接触底层 Worker 消息；
     * 3. 本方法仅封装 Worker runtime 会话生命周期，不直接创建模型历史事务，Undo/Redo 仍由上层业务提交入口负责。
     *
     * @param input runtime switch 启动参数。
     * @returns Worker 几何运行时切换句柄。
     */
    async beginGeometryRuntimeSwitch(
        input: BeginWorkerGeometryRuntimeSwitchInput,
    ): Promise<WorkerGeometryRuntimeSwitchHandle> {
        this.assertNoPendingGeometryRuntimeSwitchSession();
        this.advanceDocumentRevision();
        const runtimeSwitchRevision = this.getRevisionState().documentRevision;

        const runtimeSwitchId = createGeometryRuntimeSwitchId();
        let handleState: RuntimeSwitchHandleState = 'active';
        this.activeGeometryRuntimeSwitchSession = {
            id: runtimeSwitchId,
            documentRevision: runtimeSwitchRevision,
            phase: 'beginning',
        };

        // BEGIN 失败时不回滚 document revision：新的 revision 已经用于隔离旧异步响应，回退会破坏 stale guard 的单调性。
        await this.sendManagedRequest<void>(WMT.BEGIN_GEOMETRY_RUNTIME_SWITCH, {
            runtimeSwitchId,
            runtimeKey: input.runtimeKey,
            scope: input.scope,
        }).then(
            () => {
                if (this.activeGeometryRuntimeSwitchSession?.id === runtimeSwitchId) {
                    this.activeGeometryRuntimeSwitchSession.phase = 'active';
                }
            },
            (error) => {
                this.releaseGeometryRuntimeSwitchSession(runtimeSwitchId);
                throw error;
            },
        );

        /**
         * 校验 runtime switch 句柄仍然可用。
         *
         * @param operation 当前即将执行的操作名称。
         */
        const assertHandleCanSend = (operation: 'register' | 'batchExecute' | 'commit' | 'abort'): void => {
            if (handleState !== 'active') {
                const currentRevision = this.getRevisionState().documentRevision;
                if (handleState === 'closed' && currentRevision !== runtimeSwitchRevision) {
                    throw new Error(
                        `Worker runtime switch(${runtimeSwitchId}) document revision 已变化：begin=${runtimeSwitchRevision}, current=${currentRevision}，不能继续执行 ${operation}`,
                    );
                }

                throw new Error(`Worker runtime switch(${runtimeSwitchId}) 已经 ${handleState}，不能继续执行 ${operation}`);
            }

            const currentRevision = this.getRevisionState().documentRevision;
            if (currentRevision !== runtimeSwitchRevision) {
                handleState = 'closed';
                this.releaseGeometryRuntimeSwitchSession(runtimeSwitchId);
                throw new Error(
                    `Worker runtime switch(${runtimeSwitchId}) document revision 已变化：begin=${runtimeSwitchRevision}, current=${currentRevision}，不能继续执行 ${operation}`,
                );
            }

            const session = this.activeGeometryRuntimeSwitchSession;
            if (!session || session.id !== runtimeSwitchId || session.phase !== 'active') {
                throw new Error(`Worker runtime switch(${runtimeSwitchId}) 当前会话不可用，不能继续执行 ${operation}`);
            }
        };

        /**
         * runtime switch 请求失败后恢复本地句柄状态。
         *
         * @param operation 失败操作名称。
         */
        const restoreHandleAfterRuntimeSwitchRequestFailure = (operation: 'batchExecute' | 'commit' | 'abort'): void => {
            if (this.getRevisionState().documentRevision === runtimeSwitchRevision) {
                handleState = 'active';
                if (this.activeGeometryRuntimeSwitchSession?.id === runtimeSwitchId) {
                    this.activeGeometryRuntimeSwitchSession.phase = 'active';
                }
                return;
            }

            handleState = 'closed';
            this.releaseGeometryRuntimeSwitchSession(runtimeSwitchId);
            throw new Error(
                `Worker runtime switch(${runtimeSwitchId}) document revision 已变化：begin=${runtimeSwitchRevision}, current=${this.getRevisionState().documentRevision}，不能继续执行 ${operation}`,
            );
        };

        return {
            id: runtimeSwitchId,

            /**
             * 将导入 BRep Shape 注册到当前 staged runtime。
             *
             * @param payload 导入 BRep Shape 注册请求。
             * @returns 注册后的 Worker 几何对象信息。
             */
            registerImportedBrepShape: async (payload: RegisterImportedBrepShapePayload): Promise<RegisterImportedBrepShapeResult> => {
                assertHandleCanSend('register');

                return this.sendManagedRequest<RegisterImportedBrepShapeResult>(
                    WMT.REGISTER_IMPORTED_BREP_SHAPE_IN_RUNTIME,
                    {
                        runtimeSwitchId,
                        registration: payload,
                    },
                );
            },

            /**
             * 将一组导入 BRep Shape 原子注册到当前 staged runtime。
             *
             * @param payloads 已完成主线程 sidecar 预校验的 BRep 注册请求。
             * @returns 与请求顺序一致的 Worker 几何对象注册结果。
             */
            registerImportedBrepShapesAtomically: async (
                payloads: readonly RegisterImportedBrepShapePayload[],
                options?: {
                    readonly commitPreparedResults?: (
                        results: readonly RegisterImportedBrepShapeResult[],
                    ) => void;
                },
            ): Promise<readonly RegisterImportedBrepShapeResult[]> => {
                assertHandleCanSend('register');

                const results = await this.sendManagedRequest<RegisterImportedBrepShapeResult[]>(
                    WMT.REGISTER_IMPORTED_BREP_SHAPES_ATOMICALLY_IN_RUNTIME,
                    {
                        runtimeSwitchId,
                        registrations: payloads,
                    },
                );
                options?.commitPreparedResults?.(results);
                return results;
            },

            /**
             * 在当前 staged runtime 中批量回放命令。
             *
             * @param payload 批量回放载荷。
             * @returns staged runtime 回放后的 Worker 几何状态摘要。
             */
            batchExecute: async (payload: BatchExecutePayload): Promise<WorkerGeometryStateSummary> => {
                assertHandleCanSend('batchExecute');
                if (input.scope.kind !== 'full-document') {
                    throw new Error('scoped-subtree Worker runtime switch 暂不支持 staged batchExecute');
                }

                handleState = 'batching';
                if (this.activeGeometryRuntimeSwitchSession?.id === runtimeSwitchId) {
                    this.activeGeometryRuntimeSwitchSession.phase = 'batching';
                }

                try {
                    const summary = await this.sendManagedRequest<WorkerGeometryStateSummary>(
                        WMT.BATCH_EXECUTE_IN_RUNTIME,
                        {
                            runtimeSwitchId,
                            batch: payload,
                        },
                    );

                    handleState = 'active';
                    if (this.activeGeometryRuntimeSwitchSession?.id === runtimeSwitchId) {
                        this.activeGeometryRuntimeSwitchSession.phase = 'active';
                    }
                    return summary;
                } catch (error) {
                    restoreHandleAfterRuntimeSwitchRequestFailure('batchExecute');
                    throw error;
                }
            },

            /**
             * 提交当前 staged runtime，并返回提交后的 Worker 几何状态摘要。
             *
             * @returns 提交后的 Worker 几何状态摘要。
             */
            commit: async (
                commitInput?: WorkerGeometryRuntimeSwitchCommitInput,
            ): Promise<WorkerGeometryStateSummary> => {
                assertHandleCanSend('commit');
                handleState = 'committing';
                if (this.activeGeometryRuntimeSwitchSession?.id === runtimeSwitchId) {
                    this.activeGeometryRuntimeSwitchSession.phase = 'committing';
                }

                try {
                    const scopedBodyBindingIds = commitInput?.scopedBodyBindingIds
                        ?.map((bodyBindingId) => bodyBindingId.trim())
                        .filter((bodyBindingId) => bodyBindingId.length > 0);
                    const payload: CompleteGeometryRuntimeSwitchPayload = scopedBodyBindingIds?.length
                        ? {
                            runtimeSwitchId,
                            scopedBodyBindingIds,
                        }
                        : { runtimeSwitchId };
                    const summary = await this.sendManagedRequest<WorkerGeometryStateSummary>(
                        WMT.COMMIT_GEOMETRY_RUNTIME_SWITCH,
                        payload,
                    );

                    handleState = 'committed';
                    this.releaseGeometryRuntimeSwitchSession(runtimeSwitchId);
                    return summary;
                } catch (error) {
                    restoreHandleAfterRuntimeSwitchRequestFailure('commit');
                    throw error;
                }
            },

            /**
             * 放弃当前 staged runtime。
             *
             * @returns 放弃完成后的异步结果。
             */
            abort: async (): Promise<void> => {
                if (handleState !== 'active') {
                    throw new Error(`Worker runtime switch(${runtimeSwitchId}) 已经 ${handleState}，不能继续执行 abort`);
                }
                const isRevisionDriftCleanup = this.getRevisionState().documentRevision !== runtimeSwitchRevision;
                handleState = 'aborting';
                if (this.activeGeometryRuntimeSwitchSession?.id === runtimeSwitchId) {
                    this.activeGeometryRuntimeSwitchSession.phase = 'aborting';
                }

                /**
                 * 根据当前 revision 决定 abort 失败后是否允许重试。
                 */
                const restoreAbortAfterFailure = (): void => {
                    handleState = 'active';
                    if (this.activeGeometryRuntimeSwitchSession?.id === runtimeSwitchId) {
                        this.activeGeometryRuntimeSwitchSession.phase = 'active';
                    }
                };

                const result = await this.sendManagedRequest<RuntimeSwitchAbortResult>(
                    WMT.ABORT_GEOMETRY_RUNTIME_SWITCH,
                    { runtimeSwitchId },
                    {
                        // cleanup 不写回模型主状态，即使再次推进 revision 也必须接收真实释放结果。
                        acceptStaleResponse: true,
                    },
                ).catch((error) => {
                    restoreAbortAfterFailure();
                    throw error;
                });

                if (result.runtimeSwitchId !== runtimeSwitchId) {
                    restoreAbortAfterFailure();
                    throw new Error(`Worker runtime switch(${runtimeSwitchId}) abort 返回 ID 不匹配：${result.runtimeSwitchId}`);
                }

                if (result.aborted !== true && !isRevisionDriftCleanup) {
                    restoreAbortAfterFailure();
                    throw new Error(`Worker runtime switch(${runtimeSwitchId}) abort 未成功`);
                }

                handleState = 'aborted';
                this.releaseGeometryRuntimeSwitchSession(runtimeSwitchId);
            },
        };
    }

    /**
     * 导出当前 Worker 可访问的 STEP 文件。
     *
     * 设计说明：
     * 1. 导出可能触发 Puppy CAD 内核 写文件与 BRep 传输，必须走统一的 Worker 请求队列；
     * 2. 入口只接收结构化导出请求，具体 BRep / XDE / STEP 写出由 Worker 内部完成。
     *
     * @param payload STEP 导出请求。
     * @returns STEP 导出结果。
     */
    exportStep(payload: StepExportRequest): Promise<StepExportResult> {
        return this.sendManagedRequest<StepExportResult>(WMT.EXPORT_STEP, payload);
    }

    /**
     * 导出当前 Worker 持有的 BRep Shape 文本。
     *
     * 设计说明：
     * 1. `.puppy` 离线包需要 Worker 内的 live Puppy CAD 内核 Shape 作为权威拓扑源；
     * 2. 主线程只传入需要导出的 objectId 列表，具体 BRepTools 写文件和 FS 清理全部收口在 Worker；
     * 3. 该方法只读取几何状态，不修改建模历史或触发 Undo/Redo 事务。
     *
     * @param payload BRep 导出请求。
     * @returns BRep 导出结果。
     */
    exportBrepShapes(payload: ExportBrepShapesRequest): Promise<ExportBrepShapesResult> {
        return this.sendManagedRequest<ExportBrepShapesResult>(WMT.EXPORT_BREP_SHAPES, payload);
    }

    /**
     * 生成拉伸预览网格（不写入几何状态）
     */
    generateExtrudePreview(
        params: ExtrudeParams,
        options?: {
            tolerance?: number;
            angularTolerance?: number;
        }
    ): Promise<WorkerFeaturePreviewResult> {
        return this.sendManagedRequest<WorkerFeaturePreviewResult>(WMT.EXTRUDE_PREVIEW, {
            params,
            options
        });
    }

    /**
     * 生成圆角预览网格（不写入几何状态）。
     */
    generateFilletPreview(
        params: FilletParams,
        options?: {
            tolerance?: number;
            angularTolerance?: number;
        },
    ): Promise<WorkerFeaturePreviewResult> {
        return this.sendManagedRequest<WorkerFeaturePreviewResult>(WMT.FILLET_PREVIEW, {
            params,
            options,
        });
    }

    /**
     * 在编辑会话 sandbox 中生成圆角预览网格（不污染正式几何状态）。
     */
    generateFilletPreviewInSandbox(
        payload: FilletPreviewInSandboxPayload,
    ): Promise<WorkerFeaturePreviewResult> {
        return this.sendManagedRequest<WorkerFeaturePreviewResult>(WMT.FILLET_PREVIEW_IN_SANDBOX, payload);
    }

    /**
     * 生成倒角预览网格（不写入几何状态）。
     */
    generateChamferPreview(
        params: ChamferParams,
        options?: {
            tolerance?: number;
            angularTolerance?: number;
            preferredItemRef?: string | null;
        },
    ): Promise<WorkerFeaturePreviewResult> {
        return this.sendManagedRequest<WorkerFeaturePreviewResult>(WMT.CHAMFER_PREVIEW, {
            params,
            preferredItemRef: options?.preferredItemRef ?? null,
            options: {
                tolerance: options?.tolerance,
                angularTolerance: options?.angularTolerance,
            },
        });
    }

    /**
     * 在编辑会话 sandbox 中生成倒角预览网格（不污染正式几何状态）。
     */
    generateChamferPreviewInSandbox(
        payload: ChamferPreviewInSandboxPayload,
    ): Promise<WorkerFeaturePreviewResult> {
        return this.sendManagedRequest<WorkerFeaturePreviewResult>(WMT.CHAMFER_PREVIEW_IN_SANDBOX, payload);
    }

    /**
     * 生成抽壳预览网格（不写入几何状态）。
     */
    generateShellPreview(
        params: ShellParams,
        options?: {
            tolerance?: number;
            angularTolerance?: number;
        },
    ): Promise<WorkerFeaturePreviewResult> {
        return this.sendManagedRequest<WorkerFeaturePreviewResult>(WMT.SHELL_PREVIEW, {
            params,
            options,
        });
    }

    /**
     * 在编辑会话 sandbox 中生成抽壳预览网格（不污染正式几何状态）。
     */
    generateShellPreviewInSandbox(
        payload: ShellPreviewInSandboxPayload,
    ): Promise<WorkerFeaturePreviewResult> {
        return this.sendManagedRequest<WorkerFeaturePreviewResult>(WMT.SHELL_PREVIEW_IN_SANDBOX, payload);
    }

    /**
     * 生成筋预览网格（不写入几何状态）。
     */
    generateRibPreview(
        params: RibParams,
        options?: {
            tolerance?: number;
            angularTolerance?: number;
        },
    ): Promise<WorkerFeaturePreviewResult> {
        return this.sendManagedRequest<WorkerFeaturePreviewResult>(WMT.RIB_PREVIEW, {
            params,
            options,
        });
    }

    /**
     * 在编辑会话 sandbox 中生成筋预览网格（不污染正式几何状态）。
     */
    generateRibPreviewInSandbox(
        payload: RibPreviewInSandboxPayload,
    ): Promise<WorkerFeaturePreviewResult> {
        return this.sendManagedRequest<WorkerFeaturePreviewResult>(WMT.RIB_PREVIEW_IN_SANDBOX, payload);
    }

    /**
     * 在编辑会话 sandbox 中生成拉伸预览网格（不污染正式几何状态）。
     */
    generateExtrudePreviewInSandbox(
        payload: ExtrudePreviewInSandboxPayload,
    ): Promise<WorkerFeaturePreviewResult> {
        return this.sendManagedRequest<WorkerFeaturePreviewResult>(WMT.EXTRUDE_PREVIEW_IN_SANDBOX, payload);
    }

    /**
     * 生成旋转预览网格（不写入几何状态）
     */
    generateRevolvePreview(
        params: RevolveParams,
        options?: {
            tolerance?: number;
            angularTolerance?: number;
        }
    ): Promise<WorkerFeaturePreviewResult> {
        return this.sendManagedRequest<WorkerFeaturePreviewResult>(WMT.REVOLVE_PREVIEW, {
            params,
            options
        });
    }

    /**
     * 在编辑会话 sandbox 中生成旋转预览网格（不污染正式几何状态）。
     * @param payload 编辑态旋转预览载荷。
     * @returns 旋转预览网格。
     */
    generateRevolvePreviewInSandbox(
        payload: RevolvePreviewInSandboxPayload,
    ): Promise<WorkerFeaturePreviewResult> {
        return this.sendManagedRequest<WorkerFeaturePreviewResult>(WMT.REVOLVE_PREVIEW_IN_SANDBOX, payload);
    }

    /**
     * 查找草图闭合区域（由 core 布尔拆分得到）
     */
    resolveRevolveDragContours(
        params: RevolveParams,
    ): Promise<ResolveRevolveDragContoursResponse> {
        return this.sendManagedRequest<ResolveRevolveDragContoursResponse>(WMT.RESOLVE_REVOLVE_DRAG_CONTOURS, {
            params
        });
    }

    findSketchRegions(payload: FindSketchRegionsPayload): Promise<FindSketchRegionsResponse> {
        return this.sendManagedRequest<FindSketchRegionsResponse>(WMT.FIND_SKETCH_REGIONS, payload);
    }

    /**
     * 获取几何状态
     */
    getState(): Promise<WorkerGeometryStateSummary> {
        return this.sendManagedRequest(WMT.GET_STATE, {});
    }

    /**
     * 批量执行命令。
     * 说明：
     * 1. 该入口主要服务于特征依赖重建场景；
     * 2. 允许在执行前清空 Worker 状态，然后按给定命令顺序整批回放；
     * 3. 返回回放完成后的几何状态摘要，供上层继续同步场景 mesh。
     * @param payload 批量执行载荷。
     * @returns 执行后的几何状态摘要。
     */
    batchExecute(payload: BatchExecutePayload): Promise<WorkerGeometryStateSummary> {
        try {
            this.assertCanMutateActiveGeometry('batchExecute');
        } catch (error) {
            return Promise.reject(error);
        }
        return this.sendManagedRequest<WorkerGeometryStateSummary>(WMT.BATCH_EXECUTE, payload);
    }

    /**
     * 按 Feature 粒度执行命令段，并保留首个 Feature error 之前的成功前缀。
     *
     * 该入口会修改 active Worker 工作状态，因此与普通命令执行一样受 runtime switch
     * 占用保护；它不会替代 `batchExecute()` 的整批原子语义。
     *
     * @param payload 成功前缀批执行载荷。
     * @returns 首错位置与当前活动状态摘要。
     */
    executeFeaturePrefixBatch(
        payload: ExecuteFeaturePrefixBatchPayload,
    ): Promise<WorkerFeaturePrefixBatchResult> {
        try {
            this.assertCanMutateActiveGeometry('executeFeaturePrefixBatch');
        } catch (error) {
            return Promise.reject(error);
        }
        return this.sendManagedRequest<WorkerFeaturePrefixBatchResult>(
            WMT.EXECUTE_FEATURE_PREFIX_BATCH,
            payload,
        );
    }

    /**
     * 查询 active Worker 当前历史位置的轻量 topology。
     *
     * 查询会幂等重申本轮时间线可见性，因此同样不得与 staged runtime switch 并发；
     * 返回值只含 topology DTO，不触发三角化或 Mesh 构建。
     *
     * @param payload topology owner 与时间线可见性命令。
     * @returns 命中的 owner topology 与缺失 owner key。
     */
    queryTopologyCheckpoint(
        payload: QueryTopologyCheckpointPayload,
    ): Promise<QueryTopologyCheckpointResult> {
        try {
            this.assertCanMutateActiveGeometry('queryTopologyCheckpoint');
        } catch (error) {
            return Promise.reject(error);
        }
        return this.sendManagedRequest<QueryTopologyCheckpointResult>(
            WMT.QUERY_TOPOLOGY_CHECKPOINT,
            payload,
        );
    }

    /**
     * 查询 active Worker 中指定 Face 的 Convert Entities Wire 边界。
     *
     * @param payload topology owner 与当前 Face index。
     * @returns 完整 outer/inner Wire 边索引。
     */
    queryConvertEntitiesFaceBoundaries(
        payload: QueryConvertEntitiesFaceBoundariesPayload,
    ): Promise<QueryConvertEntitiesFaceBoundariesResult> {
        try {
            this.assertCanMutateActiveGeometry('queryConvertEntitiesFaceBoundaries');
        } catch (error) {
            return Promise.reject(error);
        }
        return this.sendManagedRequest<QueryConvertEntitiesFaceBoundariesResult>(
            WMT.QUERY_CONVERT_ENTITIES_FACE_BOUNDARIES,
            payload,
        );
    }

    /**
     * 查询 active Worker 中点击边所属的唯一直接 InnerWire。
     *
     * @param payload topology owner 与点击 edge index。
     * @returns 唯一 InnerWire；点击边不是内环边时 match 为 null。
     */
    queryConvertEntitiesInnerLoopByEdge(
        payload: QueryConvertEntitiesInnerLoopByEdgePayload,
    ): Promise<QueryConvertEntitiesInnerLoopByEdgeResult> {
        try {
            this.assertCanMutateActiveGeometry('queryConvertEntitiesInnerLoopByEdge');
        } catch (error) {
            return Promise.reject(error);
        }
        return this.sendManagedRequest<QueryConvertEntitiesInnerLoopByEdgeResult>(
            WMT.QUERY_CONVERT_ENTITIES_INNER_LOOP_BY_EDGE,
            payload,
        );
    }

    /**
     * 释放已由特征重放接管的导入基线。
     *
     * 说明：
     * 1. 该入口服务于离线包重开的显示交接——重放成功产出的特征自有实体，其同 id 导入基线为冗余，
     *    必须从 Worker 基线集彻底移除，否则后续编辑/退回清空工作态时旧基线体会重新暴露形成双体；
     * 2. 仅移除被工作态遮蔽的基线，权威结果与重放失败实体的降级基线均不受影响；
     * 3. 返回释放后的最新几何摘要，供上层据权威状态重建结果网格。
     *
     * @param payload 待释放基线的实体 ID 列表。
     * @returns 释放数量与最新几何状态摘要。
     */
    releaseFeatureOwnedBaselines(
        payload: ReleaseFeatureOwnedBaselinesPayload,
    ): Promise<{releasedCount: number; summary: WorkerGeometryStateSummary}> {
        try {
            this.assertCanMutateActiveGeometry('batchExecute');
        } catch (error) {
            return Promise.reject(error);
        }
        return this.sendManagedRequest<{releasedCount: number; summary: WorkerGeometryStateSummary}>(
            WMT.RELEASE_FEATURE_OWNED_BASELINES,
            payload,
        );
    }

    /**
     * 计算2D曲线点集
     * 使用 Puppy CAD 内核 在 Worker 中计算几何曲线
     */
    compute2DCurve(payload: Compute2DCurvePayload): Promise<Compute2DCurveResponse> {
        return this.sendManagedRequest<Compute2DCurveResponse>(WMT.COMPUTE_2D_CURVE, payload);
    }

    /**
     * 确保 Worker 已初始化
     */
    private async ensureInitialized(): Promise<void> {
        if (!this.isInitialized) {
            await this.init();
        }
    }

    /**
     * 终止 Worker
     */
    terminate(): void {
        // terminate 是页面卸载或应用退出级 Worker 出口；根文档切换重建 Viewer 时应保留 WorkerManager。
        this.pendingRequests.forEach((pending) => {
            pending.reject(new Error('Worker 已终止'));
        });
        this.pendingRequests.clear();

        if (this.worker) {
            this.worker.terminate();
        }

        this.worker = null;
        this.isInitialized = false;
        this.initPromise = null;
        this.activeGeometryRuntimeSwitchSession = null;
        this.activeWorkerInitializationToken = null;
    }

    /**
     * 获取待处理请求数量
     */
    getPendingCount(): number {
        return this.pendingRequests.size;
    }

    /**
     * 检查是否已初始化
     */
    isReady(): boolean {
        return this.isInitialized;
    }
}

// 导出单例实例（禁用自动初始化，避免页面加载时的WASM错误）
export const workerManager = new WorkerManager({ autoInit: false });
